import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getRecordList, likeRecord } from '../api/jueGuangService';
import { getStorage, STORAGE_KEYS, getUserInfo } from '@shared/api/client';
import AuthModal from '@features/auth/components/AuthModal';
import SubmitPostModal from '../components/SubmitPostModal';
import { usePageLayout } from '@app/router/index';
import { ROUTES } from '@app/router/routes';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { useLanguage } from '@shared/contexts/LanguageContext';

// ────────────────────────────────────────────
// Icons
// ────────────────────────────────────────────
const SearchIcon = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="39.5" height="39.5" rx="4" fill="#E5E5E5"/>
    <path d="M27.9833 29.5L21.1583 22.675C20.6167 23.1083 19.9938 23.4514 19.2896 23.7042C18.5854 23.9569 17.8361 24.0833 17.0417 24.0833C15.0736 24.0833 13.408 23.4017 12.0448 22.0385C10.6816 20.6753 10 19.0097 10 17.0417C10 15.0736 10.6816 13.408 12.0448 12.0448C13.408 10.6816 15.0736 10 17.0417 10C19.0097 10 20.6753 10.6816 22.0385 12.0448C23.4017 13.408 24.0833 15.0736 24.0833 17.0417C24.0833 17.8361 23.9569 18.5854 23.7042 19.2896C23.4514 19.9938 23.1083 20.6167 22.675 21.1583L29.5 27.9833L27.9833 29.5ZM17.0417 21.9167C18.3958 21.9167 19.5469 21.4427 20.4948 20.4948C21.4427 19.5469 21.9167 18.3958 21.9167 17.0417C21.9167 15.6875 21.4427 14.5365 20.4948 13.5885C19.5469 12.6406 18.3958 12.1667 17.0417 12.1667C15.6875 12.1667 14.5365 12.6406 13.5885 13.5885C12.6406 14.5365 12.1667 15.6875 12.1667 17.0417C12.1667 18.3958 12.6406 19.5469 13.5885 20.4948C14.5365 21.4427 15.6875 21.9167 17.0417 21.9167Z" fill="#625D53"/>
  </svg>
);

const CloseIcon = ({ size = 14, color = '#1C1B1F' }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.4 14L0 12.6L5.6 7L0 1.4L1.4 0L7 5.6L12.6 0L14 1.4L8.4 7L14 12.6L12.6 14L7 8.4L1.4 14Z" fill={color}/>
  </svg>
);

// ────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────
function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${yyyy}. ${mm}. ${dd}`;
}

// ────────────────────────────────────────────
// Post card
// ────────────────────────────────────────────
function isVideo(url) {
  return !!url?.match(/\.(mp4|mov|webm)(\?|$)/i);
}

function PostCard({ post, onClick, onLike }) {
  const [liked, setLiked] = useState(post.is_liked ?? false);
  const [likeCount, setLikeCount] = useState(post.like_count ?? 0);
  const [imgError, setImgError] = useState(false);
  const name = post.display_name || post.user_name || '';

  const cacheIsVideo = isVideo(post.cache_url);
  // Card thumbnail: prefer static thumbnail, fall back to cache_url only if it's an image
  const thumbSrc = post.thumbnail || (!cacheIsVideo ? post.cache_url : null);
  const showVideoIcon = cacheIsVideo || isVideo(post.thumbnail);

  async function handleLike(e) {
    e.stopPropagation();
    if (liked) return;
    setLiked(true);
    setLikeCount(c => c + 1);
    const res = await likeRecord(post.record_id, true);
    if (!res.success) {
      setLiked(false);
      setLikeCount(c => c - 1);
    } else {
      onLike?.(post.record_id, true, res.data?.like_count ?? likeCount + 1);
    }
  }

  return (
    <div
      className="bg-stone-50 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.10)] overflow-hidden flex flex-col h-full cursor-pointer active:scale-[0.98] transition-transform"
      onClick={onClick}
    >
      <div className="flex items-center gap-2 px-3 pt-3 pb-2 min-h-[52px]">
        {post.mascot_url ? (
          <img src={post.mascot_url} alt="" className="w-7 h-7 rounded-full object-cover shrink-0 bg-stone-200" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-stone-200 shrink-0" />
        )}
        <span className="text-sm font-semibold text-stone-800 leading-tight line-clamp-2">{name}</span>
      </div>
      <div className="relative mx-2 aspect-square overflow-hidden shadow-[0px_2px_4px_0px_rgba(0,0,0,0.25)] bg-stone-200">
        {thumbSrc && !imgError ? (
          <img
            src={thumbSrc}
            alt=""
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full bg-stone-200" />
        )}
        {showVideoIcon && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center">
              <span className="material-symbols-outlined text-white" style={{ fontSize: '22px', fontVariationSettings: "'FILL' 1" }}>play_arrow</span>
            </div>
          </div>
        )}
      </div>
      {/* Date + like row */}
      <div className="flex items-center justify-between px-3 pt-2">
        <span className="text-xs text-stone-400 tabular-nums">{formatDate(post.created_at)}</span>
        <button
          onClick={handleLike}
          className="flex items-center gap-1 cursor-pointer transition-all active:scale-90"
        >
          <span
            className="material-symbols-outlined"
            style={{
              fontSize: '16px',
              color: liked ? '#e53e3e' : '#1c1b1f',
              fontVariationSettings: liked ? "'FILL' 1" : "'FILL' 0",
            }}
          >
            favorite
          </span>
          {likeCount > 0 && (
            <span className="text-xs font-medium text-stone-700 tabular-nums leading-none">{likeCount}</span>
          )}
        </button>
      </div>
      <div className="px-3 pt-1 pb-3 flex-1 max-h-[7.5rem] overflow-hidden">
        <p className="text-sm font-semibold text-stone-700 leading-relaxed line-clamp-4">{post.description ?? ''}</p>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
// Single slide content
// ────────────────────────────────────────────
function SlideContent({ post, onPrev, onNext, onZoom }) {
  const [liked, setLiked] = useState(post?.is_liked ?? false);
  const [likeCount, setLikeCount] = useState(post?.like_count ?? 0);

  const imageSrc = post.cache_url || post.thumbnail || null;
  const fullName = post.display_name || post.user_name || '';

  async function handleLike() {
    if (liked) return;
    setLiked(true);
    setLikeCount(c => c + 1);
    const res = await likeRecord(post.record_id, true);
    if (res.success) {
      if (res.data.like_count != null) setLikeCount(res.data.like_count);
    } else {
      setLiked(false);
      setLikeCount(c => c - 1);
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Image — fixed square size matching upload modal */}
      <div
        className="relative w-full shrink-0 cursor-zoom-in"
        style={{ height: 'min(100vw, 600px)' }}
        onClick={() => imageSrc && onZoom?.(imageSrc, post.cache_url)}
      >
        {imageSrc
          ? <img src={imageSrc} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full bg-stone-200" />
        }
      </div>

      {/* Info — scrollable independently */}
      <div className="flex-1 overflow-y-auto">
      <div className="px-[30px] pt-4 pb-4">
        {/* Date + like row */}
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-['SF_Pro'] leading-7 text-black">
            {formatDate(post.created_at)}
          </p>
          <button
            onClick={handleLike}
            className="shrink-0 flex items-center gap-1 cursor-pointer transition-all active:scale-90 ml-2"
          >
            <span
              className="material-symbols-outlined transition-colors"
              style={{
                fontSize: '24px',
                color: liked ? '#e53e3e' : '#1c1b1f',
                fontVariationSettings: liked ? "'FILL' 1" : "'FILL' 0",
              }}
            >
              favorite
            </span>
            {likeCount > 0 && (
              <span className="text-sm text-stone-500 tabular-nums">{likeCount}</span>
            )}
          </button>
        </div>

        {/* User row */}
        <div className="flex items-center gap-2 min-w-0 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#c5c5c5] overflow-hidden shrink-0 flex items-center justify-center">
            {post.mascot_url
              ? <img src={post.mascot_url} alt="" className="w-9 h-9 object-cover" />
              : <span className="material-symbols-outlined text-white" style={{ fontSize: '18px' }}>person</span>
            }
          </div>
          <span className="text-base font-['SF_Pro'] leading-7 text-black truncate">
            {fullName}
          </span>
        </div>

        {/* Description */}
        {post.description && (
          <p className="text-base font-normal font-['SF_Pro'] leading-6 text-black mb-4">
            {post.description}
          </p>
        )}

        {/* Divider */}
        <div className="w-full h-px bg-black" />

        {/* Down arrow — at the very bottom */}
        {onNext && (
          <div className="flex justify-center py-2">
            <button onClick={onNext} className="w-10 h-10 flex items-center justify-center cursor-pointer">
              <span className="material-symbols-outlined text-stone-500" style={{ fontSize: '28px' }}>expand_more</span>
            </button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
// Detail popup (swipeable)
// ────────────────────────────────────────────
function DetailPopup({ posts, initialIndex, onClose }) {
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxIsVideo, setLightboxIsVideo] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  function handleZoom(src, cacheUrl) {
    setLightboxIsVideo(!!cacheUrl?.match(/\.(mp4|mov|webm)$/i));
    setLightboxSrc(src);
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col"
      style={{
        backgroundImage: 'url(/image/jue-guang/background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'top',
      }}
    >
      {/* Inner — max width, centered */}
      <div className="w-full h-full flex flex-col max-w-lg mx-auto relative">

        {/* Header — X + up arrow in same row, fixed height */}
        <div className="relative flex items-center justify-center h-14 px-4 shrink-0">
          {activeIndex > 0 ? (
            <button
              onClick={() => swiperRef.current?.slidePrev()}
              className="w-8 h-8 flex items-center justify-center cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
            >
              <span className="material-symbols-outlined text-stone-600" style={{ fontSize: '24px' }}>expand_less</span>
            </button>
          ) : (
            <div className="w-8 h-8" />
          )}
          <button
            onClick={onClose}
            className="absolute right-4 w-8 h-8 flex items-center justify-center cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
          >
            <CloseIcon size={16} color="#444" />
          </button>
        </div>

        {/* Swiper */}
        <div className="flex-1 min-h-0">
          <Swiper
            direction="vertical"
            initialSlide={initialIndex}
            spaceBetween={0}
            slidesPerView={1}
            onSwiper={swiper => { swiperRef.current = swiper; }}
            onSlideChange={swiper => setActiveIndex(swiper.activeIndex)}
            style={{ height: '100%', width: '100%' }}
          >
            {posts.map((post, idx) => (
              <SwiperSlide key={post.record_id} style={{ height: '100%' }}>
                <SlideContent
                  post={post}
                  onPrev={null}
                  onNext={idx < posts.length - 1 ? () => swiperRef.current?.slideNext() : null}
                  onZoom={handleZoom}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      {/* Lightbox — outside max-w wrapper, truly fullscreen */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[200] bg-black flex items-center justify-center"
          onClick={() => setLightboxSrc(null)}
        >
          <button
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center opacity-70 hover:opacity-100 cursor-pointer z-10"
            onClick={() => setLightboxSrc(null)}
          >
            <CloseIcon size={18} color="#fff" />
          </button>
          {lightboxIsVideo ? (
            <video
              src={lightboxSrc}
              controls
              controlsList="nodownload"
              onContextMenu={e => e.preventDefault()}
              className="max-w-full max-h-full"
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <img
              src={lightboxSrc}
              alt=""
              className="max-w-full max-h-full object-contain"
              onClick={e => e.stopPropagation()}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────
export default function JueGuangPage() {
  const { t, currentLanguage } = useLanguage();
  const isEn = currentLanguage.code === 'en';
  const { topicId } = useParams();
  const { state } = useLocation();
  const topicTitle = state?.topicTitle ?? '';
  const topicEnded = state?.topicEnded ?? false;
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [isMember, setIsMember] = useState(!!getStorage(STORAGE_KEYS.MEMBER_TOKEN));
  const [showAuth, setShowAuth] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [filterDate, setFilterDate] = useState('');
  const [filterName, setFilterName] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const user = getUserInfo();
  const { setLayoutOptions } = usePageLayout();
  const navigate = useNavigate();

  useEffect(() => {
    setLayoutOptions({
      hideFooter: true,
      noPaddingBottom: true,
      hideBottomNav: selectedIndex !== null || showSubmit,
      hideHeader: selectedIndex !== null || showSubmit,
    });
    return () => setLayoutOptions({});
  }, [selectedIndex, showSubmit]);

  const fetchRecords = useCallback(async ({ pageNum = 1, reset = false } = {}) => {
    const params = {
      topic_id: Number(topicId),
      page: pageNum,
      per_page: 20,
    };
    if (filterDate) params.created_at = filterDate;
    if (filterName) params.username = filterName;
    if (onlyMine) params.only_self = true;

    if (reset) {
      setLoaded(false);
      setPosts([]);
    } else if (pageNum > 1) {
      setLoadingMore(true);
    }

    const res = await getRecordList(params);
    if (res.success) {
      const items = res.data.items ?? [];
      setPosts(prev => (reset || pageNum === 1) ? items : [...prev, ...items]);
      setTotalPages(res.data.total_pages ?? 1);
    }
    setLoaded(true);
    setLoadingMore(false);
  }, [topicId, filterDate, filterName, onlyMine]);

  useEffect(() => {
    setPage(1);
    fetchRecords({ pageNum: 1, reset: true });
  }, [topicId, filterDate, filterName, onlyMine]);

  function handleAddClick() {
    if (!isMember) {
      setShowAuth(true);
    } else {
      setShowSubmit(true);
    }
  }

  function handleSubmitSuccess() {
    setPage(1);
    fetchRecords({ pageNum: 1, reset: true });
  }

  function handleCardLike(recordId, liked, likeCount) {
    setPosts(prev => prev.map(p =>
      p.record_id === recordId ? { ...p, is_liked: liked, like_count: likeCount } : p
    ));
  }

  function handleCloseSearch() {
    setShowSearch(false);
    setFilterDate('');
    setFilterName('');
    setOnlyMine(false);
  }

  function handleLoadMore() {
    const next = page + 1;
    setPage(next);
    fetchRecords({ pageNum: next });
  }

  return (
    <div
      className="min-h-screen bg-cover bg-top bg-no-repeat relative pb-24 md:pb-10"
      style={{ backgroundImage: 'url(/image/jue-guang/background.png)' }}
    >

      {/* ── Title / Search section ── */}
      <div className="max-w-4xl mx-auto px-3 sm:px-5 pt-6">
        {showSearch ? (
          <div
            className="px-5 py-[18px] flex flex-col gap-3"
            style={{
              background: 'linear-gradient(140deg, #FFC654 0%, #FBAA0F 100%)',
              boxShadow: '0px 2px 4px rgba(0,0,0,0.20)',
              borderRadius: 6,
            }}
          >
            <div className="flex items-center justify-between px-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={onlyMine}
                  onChange={e => setOnlyMine(e.target.checked)}
                  className="w-4 h-4 rounded accent-stone-700"
                />
                <span className="text-sm text-black tracking-wide">{t('jue-guang-topic.onlyMyPosts')}</span>
              </label>
              <button onClick={handleCloseSearch} className="cursor-pointer p-1">
                <CloseIcon size={14} color="#1C1B1F" />
              </button>
            </div>
            <div className="flex items-center gap-2 bg-white rounded-[10px] px-[18px] py-1 outline outline-[0.5px] outline-[#8B8B8B]">
              <input
                type="date"
                value={filterDate}
                onChange={e => setFilterDate(e.target.value)}
                className="flex-1 text-base text-stone-800 bg-transparent outline-none tracking-wide leading-7"
              />
              {filterDate && (
                <button onClick={() => setFilterDate('')} className="cursor-pointer shrink-0">
                  <CloseIcon size={12} color="#9E9D9D" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 bg-white rounded-[10px] px-[18px] py-1 outline outline-[0.5px] outline-[#8B8B8B]">
              <input
                type="text"
                value={filterName}
                onChange={e => setFilterName(e.target.value)}
                placeholder={t('jue-guang-topic.namePlaceholder')}
                className="flex-1 text-base text-stone-800 placeholder-[#9E9D9D] bg-transparent outline-none tracking-wide leading-7"
              />
              {filterName && (
                <button onClick={() => setFilterName('')} className="cursor-pointer shrink-0">
                  <CloseIcon size={12} color="#9E9D9D" />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="pt-4 pb-3 relative">
            <button
              onClick={() => state?.fromProfile ? navigate(ROUTES.JUE_GUANG) : navigate(-1)}
              className="absolute left-0 top-4 w-9 h-9 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors cursor-pointer z-10"
            >
              <span className="material-symbols-outlined text-stone-600" style={{ fontSize: '24px' }}>arrow_back</span>
            </button>
            <div className="relative flex items-center justify-center">
              <div className="flex flex-col items-center">
                <h1 className={`${isEn ? 'text-2xl' : 'text-[42px]'} font-black text-[--color-text-main]`}>{t('jue-guang.pageTitle')}</h1>
                {topicTitle && (
                  <p className="text-base font-medium text-stone-600 tracking-wide mt-0.5">{topicTitle}</p>
                )}
              </div>
              <button onClick={() => setShowSearch(true)} className="absolute right-0 cursor-pointer hover:opacity-80 transition-opacity">
                <SearchIcon />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Content area ── */}
      <div className="max-w-4xl mx-auto px-3 sm:px-5 py-4">

        {/* Not logged in prompt */}
        {!isMember && (
          <div className="bg-white rounded-2xl p-6 text-center shadow-sm mb-6" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            <span
              className="material-symbols-outlined text-amber-400 mb-3 block"
              style={{ fontVariationSettings: "'FILL' 1", fontSize: '48px' }}
            >
              lock
            </span>
            <p className="text-base font-bold text-stone-700 mb-2">{t('jue-guang-topic.membersOnlyTitle')}</p>
            <p className="text-sm text-stone-500 mb-4">{t('jue-guang-topic.membersOnlyDesc')}</p>
            <button
              onClick={() => setShowAuth(true)}
              className="inline-flex items-center gap-2 border border-amber-400 text-amber-600 bg-white text-sm font-medium px-6 py-2.5 rounded-full transition-all duration-200 hover:bg-amber-50 cursor-pointer"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>login</span>
              {t('jue-guang-topic.loginRegister')}
            </button>
          </div>
        )}

        {/* Empty state */}
        {loaded && posts.length === 0 && (
          <div className="flex items-center justify-center py-16">
            <span
              className="font-['Noto_Serif_TC'] text-base font-normal tracking-[0.2em]"
              style={{ color: 'rgba(188,49,61,0.5)' }}
            >
              {filterDate || filterName || onlyMine ? t('jue-guang-topic.noData') : t('jue-guang.comingSoon')}
            </span>
          </div>
        )}

        {/* Grid */}
        {posts.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {posts.map((post, idx) => (
              <PostCard key={post.record_id} post={post} onClick={() => setSelectedIndex(idx)} onLike={handleCardLike} />
            ))}
          </div>
        )}

        {/* Load more */}
        {loaded && page < totalPages && (
          <div className="flex justify-center pt-4">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="px-6 py-2 text-sm text-stone-600 border border-stone-300 rounded-full hover:bg-stone-100 transition-colors disabled:opacity-50 cursor-pointer"
            >
              {loadingMore ? t('jue-guang.loading') : t('jue-guang-topic.loadMore')}
            </button>
          </div>
        )}
      </div>

      {/* ── FAB: add post (hidden when topic has ended) ── */}
      {!topicEnded && (
        <button
          onClick={handleAddClick}
          className="fixed bottom-28 md:bottom-10 right-5 w-12 h-12 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 shadow-lg flex items-center justify-center cursor-pointer hover:from-amber-400 hover:to-amber-600 transition-all duration-200 active:scale-95 outline outline-1 outline-offset-[-1px] outline-white z-30"
          title="分享覺光"
        >
          <span className="material-symbols-outlined text-white" style={{ fontSize: '22px', fontVariationSettings: "'FILL' 1" }}>
            add
          </span>
        </button>
      )}

      {/* ── Modals ── */}
      <AuthModal
        open={showAuth}
        onClose={() => setShowAuth(false)}
        onSuccess={() => {
          setIsMember(true);
          setShowAuth(false);
          setShowSubmit(true);
        }}
        defaultMode="login"
      />

      <SubmitPostModal
        open={showSubmit}
        onClose={() => setShowSubmit(false)}
        onSuccess={handleSubmitSuccess}
        user={user}
        topicId={topicId}
      />

      {/* ── Detail popup ── */}
      {selectedIndex !== null && (
        <DetailPopup
          posts={posts}
          initialIndex={selectedIndex}
          onClose={() => {
            setSelectedIndex(null);
            fetchRecords({ pageNum: 1, reset: true });
          }}
        />
      )}
    </div>
  );
}
